import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PAGE_SIZE = 10;

export function usePaged(list, resetKey) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  useEffect(() => { setPage(1); }, [resetKey]);
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const current = Math.min(page, totalPages);
  const items = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return { items, page: current, setPage, totalPages, total: list.length };
}

const pageNumbers = (page, total) => {
  const set = new Set([1, total, page - 1, page, page + 1].filter((n) => n >= 1 && n <= total));
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) out.push(`gap-${n}`);
    out.push(n);
  });
  return out;
};

export function Pager({ page, setPage, totalPages, total, testid = "pager", className = "" }) {
  const [jump, setJump] = useState("");
  if (total === 0) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  const go = (n) => setPage(Math.min(totalPages, Math.max(1, n)));
  const onJump = (e) => {
    e.preventDefault();
    const n = parseInt(jump, 10);
    if (!Number.isNaN(n)) go(n);
    setJump("");
  };
  const btn = "h-8 min-w-[32px] px-2 text-[12px] border border-[#DCD9D0] bg-white flex items-center justify-center transition-colors duration-150 hover:bg-[#FBFAF3] disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div data-testid={testid} className={`flex flex-wrap items-center gap-3 px-6 py-3 border-t border-[#DCD9D0] bg-[#FBFAF3] ${className}`}>
      <div data-testid={`${testid}-range`} className="font-mono-eo text-[11px] uppercase tracking-widest text-[#8A8A80]">
        {from}–{to} of {total}
      </div>
      <div className="flex-1" />
      {totalPages > 1 && (
        <>
          <div className="flex items-center gap-1">
            <button data-testid={`${testid}-prev`} disabled={page <= 1} onClick={() => go(page - 1)} className={btn}><ChevronLeft size={14} /> Prev</button>
            {pageNumbers(page, totalPages).map((n) => typeof n === "string" ? (
              <span key={n} className="px-1 text-[#8A8A80] text-[12px]">…</span>
            ) : (
              <button
                key={n}
                data-testid={`${testid}-page-${n}`}
                onClick={() => go(n)}
                className={`${btn} ${n === page ? "!bg-[#1D3557] !border-[#1D3557] text-white" : "text-[#3A3A32]"}`}
              >{n}</button>
            ))}
            <button data-testid={`${testid}-next`} disabled={page >= totalPages} onClick={() => go(page + 1)} className={btn}>Next <ChevronRight size={14} /></button>
          </div>
          <form onSubmit={onJump} className="flex items-center gap-1">
            <span className="font-mono-eo text-[10px] uppercase tracking-widest text-[#8A8A80]">Go to</span>
            <input
              data-testid={`${testid}-jump-input`}
              type="number" min={1} max={totalPages}
              value={jump}
              onChange={(e) => setJump(e.target.value)}
              placeholder={`1-${totalPages}`}
              className="h-8 w-[70px] px-2 text-[12px] border border-[#DCD9D0] bg-white"
            />
            <button data-testid={`${testid}-jump-btn`} type="submit" className={btn}>Go</button>
          </form>
        </>
      )}
    </div>
  );
}
